import { apiService } from './api';
import { API_CONFIG, ApiResponse } from '../config/api';

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  profilePictureUrl?: string;
  createdAt?: string;
  phone?: string;
  phoneEnc?: string;
  externalProvider?: string;
  hasPassword?: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
}

export interface RegisterOptions {
  redirectOnSuccess?: boolean;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export interface ResetPasswordRequest {
  email: string;
  returnUrl?: string;
}

export interface ResetPasswordData {
  token: string;
  newPassword: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface SetPasswordData {
  newPassword: string;
}

// Auth endpoints
const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  CHANGE_PASSWORD: '/auth/change-password',
  SET_PASSWORD: '/auth/set-password',
  EXTERNAL_REGISTER: '/auth/register/external',
};

// Auth is held in an HttpOnly backend cookie. This marker preserves existing
// call-sites that only need to know whether a cookie-backed session may exist.
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const COOKIE_SESSION_MARKER = '__cookie_session__';

// Helper functions for token management
export const getToken = (): string | null => {
  return getUser() ? COOKIE_SESSION_MARKER : null;
};

export const setToken = (_token: string): void => {
  localStorage.removeItem(TOKEN_KEY);
};

export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Helper functions for user data management
export const getUser = (): User | null => {
  const userData = localStorage.getItem(USER_KEY);
  return userData ? JSON.parse(userData) : null;
};

export const setUser = (user: User): void => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const removeUser = (): void => {
  localStorage.removeItem(USER_KEY);
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => !!getToken();

// Check if token is expired
export const isTokenExpired = (token: string): boolean => {
  if (token === COOKIE_SESSION_MARKER) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch (error) {
    return true;
  }
};

export class AuthService {
  async validateToken(): Promise<boolean> {
    return Boolean(getToken());
  }

  async getCurrentUser(_authToken?: string): Promise<User | null> {
    try {
      const response = await apiService.get<User>('/user-profile');

      // Backend wraps response: { status: "success", data: { user data } }
      const userData: User = response.data as any;

      // Update stored user data
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('❌ Error fetching current user:', error);
      // Clear invalid session
      this.logout();
      return null;
    }
  }
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiService.post<any>(
        AUTH_ENDPOINTS.LOGIN,
        credentials
      );

      const user = await this.getCurrentUser();

      if (!user) {
        console.error('❌ Failed to fetch user profile');
        throw new Error('Failed to fetch user profile after login');
      }

      return { user, token: COOKIE_SESSION_MARKER };
    } catch (error) {
      console.error('❌ Login error:', error);
      // Clear any partial auth state
      removeToken();
      removeUser();

      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An unknown error occurred during login');
    }
  }

  async register(
    userData: RegisterData,
    options: RegisterOptions = {},
  ): Promise<boolean> {
    try {
      const response = await apiService.post<ApiResponse<AuthResponse>>(
        AUTH_ENDPOINTS.REGISTER,
        userData
      );

      if (response.status === 'error') {
        throw new Error(response.error || 'Registration failed');
      }

      const shouldRedirect = options.redirectOnSuccess !== false;
      if (shouldRedirect) {
        window.location.href = '/login?registered=true';
      }

      return true;
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    }
  }

  async logout(): Promise<void> {
    try {
      // Attempt to call logout endpoint if available
      try {
        await apiService.post('/auth/logout', {});
      } catch (e) {
        // Silently fail if the endpoint doesn't exist
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear auth data regardless of API call success
      removeToken();
      removeUser();
    }
  }

  async requestPasswordReset(
    email: string,
    returnUrl?: string,
  ): Promise<void> {
    try {
      const response = await apiService.post<ApiResponse<void>>(
        AUTH_ENDPOINTS.FORGOT_PASSWORD,
        {
          email,
          ...(returnUrl ? { returnUrl } : {}),
        }
      );

      if (response.status === 'error') {
        throw new Error(response.error || 'Password reset request failed');
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An unknown error occurred during password reset request');
    }
  }

  async resetPassword(data: ResetPasswordData): Promise<void> {
    try {
      const response = await apiService.post<ApiResponse<void>>(
        AUTH_ENDPOINTS.RESET_PASSWORD,
        data
      );

      if (response.status === 'error') {
        throw new Error(response.error || 'Password reset failed');
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An unknown error occurred during password reset');
    }
  }

  async changePassword(data: ChangePasswordData): Promise<void> {
    try {
      if (!getToken()) {
        throw new Error('You must be logged in to change your password');
      }

      const response = await apiService.put<ApiResponse<void>>(
        AUTH_ENDPOINTS.CHANGE_PASSWORD,
        data
      );

      if (response.status === 'error') {
        throw new Error(response.error || 'Password change failed');
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An unknown error occurred during password change');
    }
  }

  async setPassword(data: SetPasswordData): Promise<void> {
    try {
      if (!getToken()) {
        throw new Error('You must be logged in to set your password');
      }

      const response = await apiService.put<ApiResponse<void>>(
        AUTH_ENDPOINTS.SET_PASSWORD,
        data
      );

      if (response.status === 'error') {
        throw new Error(response.error || 'Set password failed');
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An unknown error occurred while setting password');
    }
  }

  // Google authentication
  async loginWithGoogle(accessToken: string): Promise<AuthResponse> {
    try {
      const response = await apiService.post<any>(
        AUTH_ENDPOINTS.EXTERNAL_REGISTER,
        {
          provider: 'google',
          accessToken: accessToken,
        }
      );

      const user = await this.getCurrentUser();

      if (!user) {
        throw new Error('Failed to fetch user profile after Google authentication');
      }

      return { user, token: COOKIE_SESSION_MARKER };
    } catch (error) {
      // Clear any partial auth state
      removeToken();
      removeUser();

      if (error instanceof Error) {
        throw new Error(error.message);
      }
      throw new Error('An unknown error occurred during Google authentication');
    }
  }
}

export const authService = new AuthService();
export default authService;
